import {Switch} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useEffect, useRef} from 'react';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {DmsDataGrid} from '@dms/shared';

import {useToggle} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

type TestDriveVehicleDataGridFilter = {
  branch?: string;
  condition?: string[];
};

interface FilterConnectorProps {
  onChange: (filter: TestDriveVehicleDataGridFilter) => void;
  onFilterChangeCallback: (onChange: (filter: TestDriveVehicleDataGridFilter) => void) => void;
}

function FilterConnector(props: FilterConnectorProps) {
  useEffect(
    function registerFilterChangeCallback() {
      props.onFilterChangeCallback(props.onChange);
    },
    [props.onChange, props.onFilterChangeCallback] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  return <></>;
}

const EXTERNAL_FILTER_ID = 'test-drive-vehicle-list-external-filter';
const PREDEFINED_COLUMNS = ['id'] as const;
const DEMO_CAR_CONDITION: string[] = ['CONDITION_DEMONSTRATION_CAR'];

interface VehicleFormProps {
  onVehicleSelect: (vehicleId: string) => void;
}

export function VehicleForm(props: VehicleFormProps) {
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const [isBranchFilterEnabled, toggleBranchFilter] = useToggle(false);
  const [isConditionFilterEnabled, toggleConditionFilter] = useToggle(false);
  const filterChangeRef = useRef<((filter: TestDriveVehicleDataGridFilter) => void) | null>(null);

  const handleFilterChangeCallback = (
    onChange: (filter: TestDriveVehicleDataGridFilter) => void
  ) => {
    filterChangeRef.current = onChange;
  };

  useEffect(
    function applyVehicleFilters() {
      if (!filterChangeRef.current) {
        return;
      }

      const currentBranchId = currentUser?.settings.branch;
      const filter: TestDriveVehicleDataGridFilter = {
        branch: isBranchFilterEnabled && currentBranchId ? currentBranchId : undefined,
        condition: isConditionFilterEnabled ? DEMO_CAR_CONDITION : undefined,
      };

      filterChangeRef.current(filter);
    },
    [isBranchFilterEnabled, isConditionFilterEnabled, currentUser?.settings.branch]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({actionKey, rowId}) => {
    if (actionKey !== 'detail' || !props.onVehicleSelect || !rowId) {
      return;
    }

    const vehicleId = Array.isArray(rowId) ? rowId[0] : rowId;

    props.onVehicleSelect(vehicleId);
  };

  return (
    <>
      <Box paddingBottom={4}>
        <HStack spacing={4}>
          <Switch
            value={isBranchFilterEnabled}
            onChange={toggleBranchFilter}
            label={i18n.t('entity.testDrive.labels.filterByBranch')}
            data-testid="filter-branch-switch"
          />
          <Switch
            value={isConditionFilterEnabled}
            onChange={toggleConditionFilter}
            label={i18n.t('entity.testDrive.labels.filterByDemoCarCategory')}
            data-testid="filter-category-switch"
          />
        </HStack>
        <div id={EXTERNAL_FILTER_ID} />
      </Box>
      <Box height="60vh" overflow="auto">
        <DmsDataGrid
          gridCode="test-drive-vehicle-list"
          filterComponent={(params) => (
            <FilterConnector
              onChange={params.onChange}
              onFilterChangeCallback={handleFilterChangeCallback}
            />
          )}
          actionCallback={actionCallback}
          externalFilterId={EXTERNAL_FILTER_ID}
          predefinedColumns={PREDEFINED_COLUMNS}
        />
      </Box>
    </>
  );
}
